/**
 * Represents the result of a validation operation.
 * This class encapsulates the outcome of a validation check, including
 * whether it passed, an optional descriptive message, and an optional code.
 */
export class ValidateResult {
  #isValid;

  #message;

  #code;

  /**
   * Creates a new ValidateResult instance.
   * @param {boolean} isValid - Indicates whether the validation passed.
   * @param {string|null} [message=null] - Optional message describing the validation result.
   * @param {string|null} [code=null] - Optional code associated with the validation result.
   */
  constructor(isValid, message = null, code = null) {
    this.#isValid = !!isValid;
    this.#message = message === null || message === undefined ? '' : String(message);
    this.#code = code === null || code === undefined ? '' : String(code);

    /* this is a real good place to debug validation issues
    if (!this.isValid) {
      console.trace('ValidateResult created:', {
        isValid: this.#isValid,
        message: this.#message,
        code: this.#code,
      });
    }
    */
  }

  /**
   * Gets whether the validation is valid.
   * @returns {boolean} True if the validation passed, false otherwise.
   */
  get isValid() {
    return this.#isValid;
  }

  /**
   * Gets the message associated with the validation result.
   * @returns {string} The descriptive message for the validation result.
   */
  get message() {
    return this.#message;
  }

  /**
   * Sets the message associated with the validation result.
   * @param {string} value - The new message to set.
   */
  set message(value) {
    this.#message = value;
  }

  /**
   * Gets the code associated with the validation result.
   * @returns {string} The code for the validation result.
   */
  get code() {
    return this.#code;
  }
}
